import React, {
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';

export type THomeAddress = {
    HomeAddressLine1: string;
    HomeAddressLine2: string;
    HomeCity: string;
    HomeState: string;
    HomeZipCode: string;
};

interface IHomeAddressPayload {
    showHomeAddress: boolean,
    setShowHomeAddress: (value : boolean) => void
    homeAddress: THomeAddress;
    setSingleValue(name: keyof THomeAddress): (newValue: string) => void;
    batchSetValues(payload: Partial<THomeAddress>): void;
}

const defaultHomeAddress: THomeAddress = {
    HomeAddressLine1: '',
    HomeAddressLine2: '',
    HomeCity: '',
    HomeState: '',
    HomeZipCode: '',
};

const HomeAddressContext = createContext<IHomeAddressPayload>({
    showHomeAddress: false,
    setShowHomeAddress: () => {},
    homeAddress: defaultHomeAddress,
    setSingleValue: () => () => {},
    batchSetValues: () => {},
});

export default function HomeAddressProvider({
    children,
}: {
    children: ReactNode;
}) {
    const [showHomeAddress, setShowHomeAddress] = useState<boolean>(false);
    const [homeAddress, setHomeAddress] =
        useState<THomeAddress>(defaultHomeAddress);

    const setSingleValue = (name: keyof THomeAddress) => (newValue: string) => {
        setHomeAddress(current => {
            return {
                ...current,
                [name]: newValue,
            };
        });
    };

    const batchSetValues = (
        payload: Partial<THomeAddress>,
    ) => {
        setHomeAddress(current => {
            return {
                ...current,
                ...payload,
            };
        });
    };

    return (
        <HomeAddressContext.Provider
            value={useMemo(
                () => ({
                    setShowHomeAddress,
                    showHomeAddress,
                    homeAddress,
                    setSingleValue,
                    batchSetValues,
                }),
                [homeAddress, showHomeAddress, setShowHomeAddress],
            )}
        >
            {children}
        </HomeAddressContext.Provider>
    );
}

export const useHomeAddress = () => useContext(HomeAddressContext);
